import {
  cancelAnimation,
  runOnJS,
  runOnUI,
  type SharedValue,
  useSharedValue,
  withDelay,
  withSpring,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import { WELCOME_GALAXY_CORE_Y_FRACTION } from "@-/dev.improve.haniverse.routes.nav.app.auth/welcome-galaxy-timeline";
import type { OnboardingMapFrame } from "@-/dev.improve.haniverse.routes.nav.app.onboarding.handoff";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { onboardingTransitionCompletion } from "#onboarding-transition-completion";
import { getOnboardingGlobeGeometry, onboardingGlobeRotation } from "#onboarding-globe-geometry";
import type { OnboardingGlobeMotion } from "#onboarding-globe-motion";
import { onboardingGlobeRingBounds } from "#onboarding-globe-paths";

/** Where the globe sits for each part of the onboarding. */
export type OnboardingGlobeLayout = "arrival" | "corner" | "raised";

export type OnboardingGlobeViewport = {
  /** The toolbar buttons' diameter: the question pages' header row is that tall. */
  headerDiameter: number;
  height: number;
  /** The safe area's top inset, below which the header row sits. */
  topInset: number;
  width: number;
};

/** The header row's gap below the safe area, and the screen edges' gutter (the toolbar's). */
export const ONBOARDING_HEADER_INSET = 9;

/**
 * How far the camera ends up from the Earth's centre, in Earth radii: close enough that only a cap
 * about 37° in radius around China is in view, so China fills most of the globe (about 83% of its
 * width) with a sliver of its neighbours at the rim.
 */
export const ONBOARDING_GLOBE_DISTANCE = 1.25;
/** Where the camera starts: far enough that nearly a whole hemisphere is in view. */
const ARRIVAL_CAMERA_DISTANCE = 7;
/**
 * How far the camera starts from the Earth (its size), in the distances it ends at: near enough
 * that the Earth swells out of the galaxy's core at once, while the dive into it still dissolves.
 */
const ARRIVAL_SIZE_DISTANCE = 6;
/** Degrees the Earth turns as it comes in, before it settles facing China. */
const ARRIVAL_SPIN = 420;
const ARRIVAL_DURATION_MS = 3400;
/** The spin outlasts the approach, so the Earth is still turning once it is close. */
const SPIN_DURATION_MS = 4600;
const ONBOARDING_GLOBE_LAYOUT_DURATION_MS = 700;
/** Each step on spins the globe round once, and back to China. */
const SPIN_DURATION_MS_PER_TURN = 1500;
/**
 * A press swells the globe and lets it bounce back, as iOS's interactive glass buttons do: quickly
 * up under the finger, then back with a spring that overshoots a little.
 */
const PRESSED_SCALE = 1.12;
const PRESS_IN_SPRING = { damping: 20, stiffness: 420 };
const PRESS_OUT_SPRING = { damping: 12, stiffness: 300 };
/** How far the stars spread past the edges by the time the Earth has arrived. */
const ARRIVAL_STAR_SPREAD = 1.8;
const LANDING_CENTER_DURATION_MS = 650;
const LANDING_ZOOM_DURATION_MS = 1500;
const LANDING_MORPH_DURATION_MS = 1000;
/** The morph begins this far into the zoom, so the globe flows into the map without a stop. */
const LANDING_MORPH_START = 0.55;

/**
 * A camera flying in from `ARRIVAL_SIZE_DISTANCE` and braking hard: the Earth's size is the
 * inverse of the distance, so it grows out of a point, swells, then settles.
 */
function arrivalEasing(progress: number): number {
  "worklet";
  const travelled = 1 - (1 - progress) ** 3;
  const distance = ARRIVAL_SIZE_DISTANCE + (1 - ARRIVAL_SIZE_DISTANCE) * travelled;
  return (1 / distance - 1 / ARRIVAL_SIZE_DISTANCE) / (1 - 1 / ARRIVAL_SIZE_DISTANCE);
}

/** Easing out (quadratic): fast at first, settling gently. */
function settle(progress: number): number {
  "worklet";
  return 1 - (1 - progress) ** 2;
}

/** Easing in and out (cubic): a glide from rest to rest. */
function glide(progress: number): number {
  "worklet";
  return progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
}

export function getOnboardingGlobeLayout(
  layout: OnboardingGlobeLayout,
  { headerDiameter, height, topInset, width }: OnboardingGlobeViewport,
): { centerX: number; centerY: number; radius: number } {
  const fullRadius = Math.min(width * 0.36, height * 0.22);
  switch (layout) {
    case "arrival": {
      return { centerX: width / 2, centerY: height * 0.44, radius: fullRadius };
    }
    case "raised": {
      return { centerX: width / 2, centerY: topInset + 36 + fullRadius, radius: fullRadius };
    }
    case "corner": {
      // Level with the back button, in the toolbar's own top-right slot, and the same size.
      const radius = headerDiameter / 2;
      return {
        centerX: width - ONBOARDING_HEADER_INSET - radius,
        centerY: topInset + ONBOARDING_HEADER_INSET + headerDiameter / 2,
        radius,
      };
    }
  }
}

/**
 * Where the globe has to be, facing China, for China's outline to cover the map's mainland: the
 * same width, and the same middle. (The two projections differ a little, which the morph then
 * takes up.)
 */
export function getOnboardingGlobeLandingDisk(
  mapFrame: OnboardingMapFrame,
  map: { height: number; width: number },
): { centerX: number; centerY: number; radius: number } {
  const { china, chinaCenter } = getOnboardingGlobeGeometry();
  const [minX, minY, maxX, maxY] = onboardingGlobeRingBounds(china, {
    distance: ONBOARDING_GLOBE_DISTANCE,
    rotation: onboardingGlobeRotation(chinaCenter),
  });
  const radius = (map.width * mapFrame.scale) / Math.max(maxX - minX, 0.0001);
  return {
    centerX: mapFrame.x + (map.width * mapFrame.scale) / 2 - ((minX + maxX) / 2) * radius,
    centerY: mapFrame.y + (map.height * mapFrame.scale) / 2 - ((minY + maxY) / 2) * radius,
    radius,
  };
}

/**
 * Where the map is expected to frame the mainland before it has said (`onboardingMapFrame`): the
 * whole width less its padding, a little above the middle.
 */
export function getFallbackOnboardingMapFrame(
  { height, width }: OnboardingGlobeViewport,
  map: { height: number; width: number },
): OnboardingMapFrame {
  const scale = (width - 32) / map.width;
  return { scale, x: 16, y: height * 0.42 - (map.height * scale) / 2 };
}

function setNow(value: SharedValue<number>, to: number) {
  "worklet";
  cancelAnimation(value);
  value.set(to);
}

/**
 * The globe's motion (`OnboardingGlobeMotion`) and the moves the onboarding makes with it: the
 * arrival from deep space, closing in until China fills the globe; moving to each page's place; a
 * spin with each step on; a bounce under the finger; and the landing, which zooms in on China and
 * morphs it into the Learn map's mainland at `mapFrame`.
 */
export function useOnboardingGlobe({
  onTransitionFailed,
  reduceMotion,
  viewport,
}: {
  onTransitionFailed: () => void;
  reduceMotion: boolean;
  viewport: OnboardingGlobeViewport;
}) {
  const geometry = getOnboardingGlobeGeometry();
  const { chinaCenter } = geometry;
  const arrival = getOnboardingGlobeLayout("arrival", viewport);
  const centerX = useSharedValue(arrival.centerX);
  // It comes out of the welcome galaxy's core, where the dive into the galaxy was heading.
  const centerY = useSharedValue(
    reduceMotion ? arrival.centerY : viewport.height * WELCOME_GALAXY_CORE_Y_FRACTION,
  );
  const radius = useSharedValue(reduceMotion ? arrival.radius : 0);
  const distance = useSharedValue(
    reduceMotion ? ONBOARDING_GLOBE_DISTANCE : ARRIVAL_CAMERA_DISTANCE,
  );
  const longitude = useSharedValue(
    reduceMotion ? chinaCenter.longitude : chinaCenter.longitude - ARRIVAL_SPIN,
  );
  const latitude = useSharedValue(reduceMotion ? chinaCenter.latitude : 0);
  const morph = useSharedValue(0);
  const pressScale = useSharedValue(1);
  const worldOpacity = useSharedValue(1);
  const starSpread = useSharedValue(reduceMotion ? ARRIVAL_STAR_SPREAD : 1);
  const layoutTransition = useRef<ReturnType<typeof onboardingTransitionCompletion> | null>(null);
  useEffect(() => () => layoutTransition.current?.cancel(), []);
  const motion = useMemo<OnboardingGlobeMotion>(
    () => ({
      centerX,
      centerY,
      distance,
      latitude,
      longitude,
      morph,
      pressScale,
      radius,
      starSpread,
      worldOpacity,
    }),
    [
      centerX,
      centerY,
      distance,
      latitude,
      longitude,
      morph,
      pressScale,
      radius,
      starSpread,
      worldOpacity,
    ],
  );

  /**
   * Flies in from deep space out of the galaxy's core, the Earth turning until it faces China,
   * close up.
   */
  const arrive = useCallback(() => {
    layoutTransition.current?.cancel();
    if (reduceMotion) {
      return;
    }
    const approach = { duration: ARRIVAL_DURATION_MS, easing: arrivalEasing };
    const turn = { duration: SPIN_DURATION_MS, easing: settle };
    radius.set(withTiming(arrival.radius, approach));
    centerY.set(withTiming(arrival.centerY, approach));
    starSpread.set(withTiming(ARRIVAL_STAR_SPREAD, approach));
    distance.set(withTiming(ONBOARDING_GLOBE_DISTANCE, turn));
    longitude.set(withTiming(chinaCenter.longitude, turn));
    latitude.set(withTiming(chinaCenter.latitude, turn));
  }, [
    arrival.centerY,
    arrival.radius,
    centerY,
    chinaCenter,
    distance,
    latitude,
    longitude,
    radius,
    reduceMotion,
    starSpread,
  ]);

  /** Moves the globe to `layout`'s place, still facing China. */
  const moveTo = useCallback(
    (layout: OnboardingGlobeLayout, onReady: () => void = () => {}) => {
      layoutTransition.current?.cancel();
      const to = getOnboardingGlobeLayout(layout, viewport);
      const transition = onboardingTransitionCompletion({
        onFailed: onTransitionFailed,
        onReady,
        parts: 3,
        reduceMotion,
        snapToEnd: (onSnapped) => {
          runOnUI(() => {
            setNow(centerX, to.centerX);
            setNow(centerY, to.centerY);
            setNow(radius, to.radius);
            runOnJS(onSnapped)();
          })();
        },
      });
      layoutTransition.current = transition;
      if (reduceMotion) {
        return;
      }
      const { completePart } = transition;
      const timing = { duration: ONBOARDING_GLOBE_LAYOUT_DURATION_MS, easing: glide };
      centerX.set(
        withTiming(to.centerX, timing, (finished) => {
          runOnJS(completePart)(0, finished === true);
        }),
      );
      centerY.set(
        withTiming(to.centerY, timing, (finished) => {
          runOnJS(completePart)(1, finished === true);
        }),
      );
      radius.set(
        withTiming(to.radius, timing, (finished) => {
          runOnJS(completePart)(2, finished === true);
        }),
      );
    },
    [centerX, centerY, onTransitionFailed, radius, reduceMotion, viewport],
  );

  /**
   * Spins the globe round once and back to facing China, as the learner steps on. A spin under way
   * (or the arrival's) is left to finish.
   */
  const spin = useCallback(() => {
    if (reduceMotion) {
      return;
    }
    const start = chinaCenter.longitude;
    if (longitude.get() !== start) {
      return;
    }
    longitude.set(
      withTiming(
        start + 360,
        { duration: SPIN_DURATION_MS_PER_TURN, easing: glide },
        (finished) => {
          "worklet";
          // A full turn faces China again, so the angle simply returns to where it started. An
          // interrupted spin (the landing turning the globe to China) leaves the angle to that.
          if (finished === true) {
            longitude.set(start);
          }
        },
      ),
    );
  }, [chinaCenter, longitude, reduceMotion]);

  /** The globe pressed (`true`) and let go (`false`): it bounces, and does nothing else. */
  const press = useCallback(
    (isPressed: boolean) => {
      if (reduceMotion) {
        return;
      }
      pressScale.set(
        withSpring(isPressed ? PRESSED_SCALE : 1, isPressed ? PRESS_IN_SPRING : PRESS_OUT_SPRING),
      );
    },
    [pressScale, reduceMotion],
  );

  /**
   * The landing: back to the middle facing China exactly, then in close until China covers the
   * map's mainland at `mapFrame`, while China morphs onto the map's own outline and the rest of
   * the world falls away. Calls `onLanded` once China is the map's.
   */
  const land = useCallback(
    (mapFrame: OnboardingMapFrame, onLanded: () => void) => {
      layoutTransition.current?.cancel();
      const target = getOnboardingGlobeLandingDisk(mapFrame, geometry.map);
      if (reduceMotion) {
        setNow(longitude, chinaCenter.longitude);
        setNow(latitude, chinaCenter.latitude);
        setNow(distance, ONBOARDING_GLOBE_DISTANCE);
        setNow(centerX, target.centerX);
        setNow(centerY, target.centerY);
        setNow(radius, target.radius);
        setNow(worldOpacity, 0);
        setNow(morph, 1);
        onLanded();
        return;
      }
      const middle = getOnboardingGlobeLayout("arrival", viewport);
      const center = { duration: LANDING_CENTER_DURATION_MS, easing: glide };
      const zoom = { duration: LANDING_ZOOM_DURATION_MS, easing: glide };
      longitude.set(withTiming(chinaCenter.longitude, center));
      latitude.set(withTiming(chinaCenter.latitude, center));
      distance.set(withTiming(ONBOARDING_GLOBE_DISTANCE, center));
      // From wherever it was (the corner), to the middle, then in on China.
      centerX.set(
        withTiming(middle.centerX, center, () => {
          "worklet";
          centerX.set(withTiming(target.centerX, zoom));
        }),
      );
      centerY.set(
        withTiming(middle.centerY, center, () => {
          "worklet";
          centerY.set(withTiming(target.centerY, zoom));
        }),
      );
      radius.set(
        withTiming(middle.radius, center, () => {
          "worklet";
          radius.set(withTiming(target.radius, zoom));
        }),
      );
      const zoomStart = LANDING_CENTER_DURATION_MS;
      const fadeStart = zoomStart + LANDING_ZOOM_DURATION_MS * 0.35;
      const morphStart = zoomStart + LANDING_ZOOM_DURATION_MS * LANDING_MORPH_START;
      starSpread.set(withDelay(zoomStart, withTiming(ARRIVAL_STAR_SPREAD * 2.5, zoom)));
      worldOpacity.set(
        withDelay(
          fadeStart,
          withTiming(0, {
            duration: morphStart - fadeStart + LANDING_MORPH_DURATION_MS,
            easing: glide,
          }),
        ),
      );
      morph.set(
        withDelay(
          morphStart,
          withTiming(1, { duration: LANDING_MORPH_DURATION_MS, easing: glide }, (finished) => {
            "worklet";
            if (finished === true) {
              runOnJS(onLanded)();
            }
          }),
        ),
      );
    },
    [
      centerX,
      centerY,
      chinaCenter,
      distance,
      geometry.map,
      latitude,
      longitude,
      morph,
      radius,
      reduceMotion,
      starSpread,
      viewport,
      worldOpacity,
    ],
  );

  return { arrive, land, motion, moveTo, press, spin };
}

/** How long `land` takes, for the deadline that makes sure the onboarding moves on regardless. */
export const ONBOARDING_GLOBE_LANDING_DURATION_MS =
  LANDING_CENTER_DURATION_MS +
  LANDING_ZOOM_DURATION_MS * LANDING_MORPH_START +
  LANDING_MORPH_DURATION_MS;
